import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { 
  DollarSign, 
  TrendingUp, 
  Calendar, 
  Leaf, 
  Sun, 
  Battery,
  Zap,
  PiggyBank,
  Clock,
  TreePine,
  Sparkles,
  ArrowRight,
  Info,
  Gift,
  X,
  Tag,
  PartyPopper,
  Percent,
  BadgePercent
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Calculate indicative pricing based on option and fetched prices
const calculatePricing = (option, productType, solarPrices, batteryPrices) => {
  // Default fallback prices (per unit)
  const defaultSolarPricePerKw = 900;
  const defaultBatteryPricePerKwh = 900;
  
  let nowPrice = 0;
  let wasPrice = 0;
  let systemDescription = '';
  
  // Helper to get price from pricing list
  const getSolarPrices = (sizeKw) => {
    const roundedSize = Math.round(sizeKw);
    const priceItem = solarPrices.find(p => p.size_kw === roundedSize);
    if (priceItem) {
      return {
        was: priceItem.was_price || priceItem.price + 2000 || roundedSize * defaultSolarPricePerKw + 2000,
        now: priceItem.now_price || priceItem.price || roundedSize * defaultSolarPricePerKw
      };
    }
    return {
      was: roundedSize * defaultSolarPricePerKw + 2000,
      now: roundedSize * defaultSolarPricePerKw
    };
  };
  
  const getBatteryPrices = (sizeKwh) => {
    const priceItem = batteryPrices.find(p => p.size_kwh === sizeKwh);
    if (priceItem) {
      return {
        was: priceItem.was_price || priceItem.price + 2000 || sizeKwh * defaultBatteryPricePerKwh + 2000,
        now: priceItem.now_price || priceItem.price || sizeKwh * defaultBatteryPricePerKwh
      };
    }
    return {
      was: sizeKwh * defaultBatteryPricePerKwh + 2000,
      now: sizeKwh * defaultBatteryPricePerKwh
    };
  };
  
  if (productType === 'solar') {
    // Use average of min/max system size
    const avgSize = Math.round((option.systemSizeMin + option.systemSizeMax) / 2);
    const prices = getSolarPrices(avgSize);
    wasPrice = prices.was;
    nowPrice = prices.now;
    systemDescription = `${option.systemSizeMin}-${option.systemSizeMax}kW Solar System`;
  } else if (productType === 'battery') {
    if (option.solarSizeKw) {
      // Solar + Battery package
      const solarPrices = getSolarPrices(option.solarSizeKw);
      const batteryPricesData = getBatteryPrices(option.batterySizeKwh);
      wasPrice = solarPrices.was + batteryPricesData.was;
      nowPrice = solarPrices.now + batteryPricesData.now;
      systemDescription = `${option.solarSizeKw}kW Solar + ${option.batterySizeKwh}kWh Battery`;
    } else {
      // Battery only
      const prices = getBatteryPrices(option.batterySizeKwh);
      wasPrice = prices.was;
      nowPrice = prices.now;
      systemDescription = `${option.batterySizeKwh}kWh Battery System`;
    }
  }
  
  // Calculate discount (difference between was and now)
  const discount = wasPrice - nowPrice;
  
  const annualSavings = option.annualSavings;
  const paybackYears = Math.round((nowPrice / annualSavings) * 10) / 10;
  const twentyFiveYearSavings = annualSavings * 25;
  const netBenefit = twentyFiveYearSavings - nowPrice;
  
  // Environmental impact (rough estimates)
  const co2SavedPerYear = Math.round(annualSavings * 0.85); // kg CO2 per $ saved (rough)
  const treesEquivalent = Math.round(co2SavedPerYear / 21); // ~21kg CO2 per tree/year
  
  return {
    systemPrice: nowPrice,
    originalPrice: wasPrice,
    discount,
    systemDescription,
    annualSavings,
    paybackYears,
    twentyFiveYearSavings,
    netBenefit,
    co2SavedPerYear,
    treesEquivalent,
    monthlyPayment: Math.round(nowPrice / 60), // 5-year finance estimate
  };
};

export const IndicativePricing = ({ option, productType, onGetBestPrice, onBack }) => {
  const [showPromo, setShowPromo] = useState(false);
  const [solarPrices, setSolarPrices] = useState([]);
  const [batteryPrices, setBatteryPrices] = useState([]);
  const [pricesLoaded, setPricesLoaded] = useState(false);

  // Fetch pricing data on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/pricing`);
        setSolarPrices(response.data.solar_pricing || []);
        setBatteryPrices(response.data.battery_pricing || []);
      } catch (err) {
        console.error('Error fetching pricing:', err);
        // Will use default pricing calculation
      } finally {
        setPricesLoaded(true);
      }
    };
    fetchPricing();
  }, []);

  // Calculate pricing with fetched prices
  const pricing = calculatePricing(option, productType, solarPrices, batteryPrices);

  // Show promo popup after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPromo(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Handle promo CTA click
  const handlePromoCTA = () => {
    setShowPromo(false);
    onGetBestPrice();
  };
  
  return (
    <div className="space-y-3 relative">
      {/* Promo Popup Overlay */}
      {showPromo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scaleIn">
            {/* Close button */}
            <button
              onClick={() => setShowPromo(false)}
              className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/80 hover:bg-white transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>

            {/* Promo Header */}
            <div 
              className="px-6 py-3 text-center"
              style={{ background: 'linear-gradient(to right, #F7941D, #F59E0B)' }}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <PartyPopper className="w-4 h-4 text-white" />
                <span className="text-white/90 text-xs font-medium uppercase tracking-wide">Limited Time Offer</span>
                <PartyPopper className="w-4 h-4 text-white" style={{ transform: 'scaleX(-1)' }} />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white">
                EXCLUSIVE PROMO
              </h3>
            </div>

            {/* Promo Content with Ollie */}
            <div className="p-5 text-center relative">
              {/* Ollie the Owl - positioned to point at the offer */}
              <div className="flex items-start justify-center gap-4">
                {/* Ollie Image */}
                <div className="flex-shrink-0 -mt-2">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/6282024c-b688-4eaf-8f07-5149a7eabc51/images/ee9ffc55edef237df600c23bc22480d1c337f8f284d26591ee11a5093935d446.png" 
                    alt="Ollie the Owl"
                    className="w-24 h-24 object-contain"
                  />
                </div>

                {/* Offer Content */}
                <div className="flex-1 text-left">
                  {/* Discount Badge */}
                  <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1 rounded-full mb-2">
                    <Tag className="w-3 h-3" />
                    <span className="font-semibold text-sm">Special Discount</span>
                  </div>

                  {/* Main Offer */}
                  <div className="mb-2">
                    <p className="text-gray-600 text-xs mb-1">Get a further</p>
                    <div className="text-4xl md:text-5xl font-bold text-cyo-orange">
                      $1,000
                    </div>
                    <p className="text-base font-semibold text-gray-800">
                      OFF our best range
                    </p>
                  </div>
                </div>
              </div>

              {/* Urgency Text */}
              <p className="text-gray-500 text-xs mt-3 mb-4">
                This offer is only available when you request a quote today!
              </p>

              {/* Flashing CTA Button */}
              <Button
                onClick={handlePromoCTA}
                className="w-full text-white font-semibold px-6 py-4 text-base rounded-xl transition-all group"
                style={{
                  animation: 'flash-attention 1.5s ease-in-out infinite',
                  backgroundColor: '#F7941D',
                }}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Claim My $1,000 Discount
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <p className="text-xs text-gray-400 mt-2">
                No obligation • 100% free quote
              </p>
            </div>

            {/* Bottom Accent */}
            <div className="h-1 bg-gradient-to-r from-cyo-orange via-amber-400 to-cyo-orange" />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium mb-1">
          <Sparkles className="w-3 h-3" />
          Your Personalised Estimate
        </div>
        <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-0.5">
          Here's What You Could Save
        </h2>
        <p className="text-gray-500 text-xs">
          Based on your {productType === 'solar' ? 'electricity usage' : 'solar setup'} and location
        </p>
      </div>

      {/* Main Pricing Card */}
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 rounded-xl p-3 relative">
        {/* ON SALE Badge */}
        <div className="absolute -top-2 -right-2 z-10">
          <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1 animate-pulse">
            <BadgePercent className="w-3 h-3" />
            ON SALE
          </div>
        </div>

        <div className="text-center mb-3">
          <p className="text-xs text-gray-600 mb-2">{pricing.systemDescription}</p>
          
          {/* Price Display with Strikethrough - PROMINENT */}
          <div className="flex flex-col items-center bg-white rounded-xl p-4 shadow-inner">
            {/* Original Price - Struck through */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-base font-medium text-gray-400">Was:</span>
              <span className="text-2xl md:text-3xl font-bold text-gray-400 line-through">${pricing.originalPrice.toLocaleString()}</span>
            </div>
            
            {/* Sale Price - BIG AND BOLD */}
            <div className="flex items-center gap-3">
              <span className="text-lg font-semibold text-green-600">Now:</span>
              <span className="text-4xl md:text-5xl font-extrabold text-green-600">${pricing.systemPrice.toLocaleString()}</span>
            </div>
            
            {/* Save Badge - Prominent */}
            <div className="mt-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm md:text-base font-bold px-4 py-1.5 rounded-full shadow-lg animate-pulse">
              YOU SAVE ${pricing.discount.toLocaleString()}!
            </div>
          </div>
          
          <p className="text-xs text-gray-500 mt-2">
            or from <span className="font-semibold text-cyo-orange">${pricing.monthlyPayment}/month</span> with finance
          </p>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-4 gap-2 mb-2">
          {/* Annual Savings */}
          <div className="bg-white rounded-lg p-2 text-center shadow-sm">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-0.5">
              <PiggyBank className="w-3 h-3 text-green-600" />
            </div>
            <p className="text-base font-bold text-green-600">${pricing.annualSavings.toLocaleString()}</p>
            <p className="text-[9px] text-gray-500">Yearly Savings</p>
          </div>

          {/* Payback Period */}
          <div className="bg-white rounded-lg p-2 text-center shadow-sm">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-0.5">
              <Clock className="w-3 h-3 text-blue-600" />
            </div>
            <p className="text-base font-bold text-blue-600">{pricing.paybackYears}y</p>
            <p className="text-[9px] text-gray-500">Payback</p>
          </div>

          {/* 25-Year Savings */}
          <div className="bg-white rounded-lg p-2 text-center shadow-sm">
            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-0.5">
              <TrendingUp className="w-3 h-3 text-purple-600" />
            </div>
            <p className="text-base font-bold text-purple-600">${Math.round(pricing.twentyFiveYearSavings / 1000)}k</p>
            <p className="text-[9px] text-gray-500">25yr Savings</p>
          </div>

          {/* Net Benefit */}
          <div className="bg-white rounded-lg p-2 text-center shadow-sm">
            <div className="w-6 h-6 bg-cyo-orange/20 rounded-full flex items-center justify-center mx-auto mb-0.5">
              <Zap className="w-3 h-3 text-cyo-orange" />
            </div>
            <p className="text-base font-bold text-cyo-orange">${Math.round(pricing.netBenefit / 1000)}k</p>
            <p className="text-[9px] text-gray-500">Net Profit</p>
          </div>
        </div>

        {/* Environmental Impact */}
        <div className="bg-white/70 rounded-lg p-1.5 flex items-center justify-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <Leaf className="w-3 h-3 text-green-500" />
            <span className="font-semibold text-gray-900">{pricing.co2SavedPerYear.toLocaleString()}kg</span>
            <span className="text-[9px] text-gray-500">CO₂/yr</span>
          </div>
          <div className="h-4 w-px bg-gray-200" />
          <div className="flex items-center gap-1">
            <TreePine className="w-3 h-3 text-green-500" />
            <span className="font-semibold text-gray-900">{pricing.treesEquivalent}</span>
            <span className="text-[9px] text-gray-500">trees</span>
          </div>
        </div>
      </div>

      {/* Value Props - Single Row */}
      <div className="flex justify-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <Gift className="w-3 h-3 text-green-600" />
          <span className="text-gray-700">Rebates Included</span>
        </div>
        <div className="flex items-center gap-1">
          <Sun className="w-3 h-3 text-blue-600" />
          <span className="text-gray-700">25yr Warranty</span>
        </div>
        <div className="flex items-center gap-1">
          <Zap className="w-3 h-3 text-purple-600" />
          <span className="text-gray-700">Expert Install</span>
        </div>
      </div>

      {/* Disclaimer - Very Compact */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
        <p className="text-[10px] text-gray-700 flex items-center gap-1">
          <Info className="w-3 h-3 text-blue-500 flex-shrink-0" />
          <span><span className="font-semibold">Indicative estimate.</span> Actual pricing varies. Our team will provide an accurate quote.</span>
        </p>
      </div>

      {/* CTA Section - Compact */}
      <div className="bg-gradient-to-r from-cyo-orange/10 to-amber-100/50 rounded-xl p-3 text-center">
        <h3 className="text-base font-bold text-gray-900 mb-1">
          Want the best price we can offer?
        </h3>
        <p className="text-gray-600 text-xs mb-3">
          We'll calculate your best possible price including available discounts.
        </p>
        
        <Button
          onClick={onGetBestPrice}
          className="text-white font-semibold px-6 py-4 text-base rounded-xl transition-all group"
          style={{
            animation: 'flash-attention 1.5s ease-in-out infinite',
            backgroundColor: '#F7941D',
          }}
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Send Me The Best Price
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
        
        <p className="text-[9px] text-gray-500 mt-2">
          No obligation • Free proposal • Within 24 hours
        </p>
      </div>

      {/* Back link */}
      <div className="text-center">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="text-gray-500 hover:text-gray-700 py-0.5 text-xs"
        >
          ← Back to options
        </Button>
      </div>
    </div>
  );
};
